/**
 * Keeps a person on their language's version of the public pages while they
 * move around inside the app. main.tsx does this on a full page load; this
 * does it for in-app navigation, so opening the landing page from the
 * dashboard in Arabic shows /ar, not the English page with Arabic buttons.
 *
 * Same rule as main.tsx: only the English originals of pages that exist in
 * every language, never "/" for someone signed in (that is their dashboard),
 * and never a page already under a prefix (someone chose that language).
 */
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppLocale } from "./app";
import { DEFAULT_LOCALE, isTranslatedPath, localizePath, splitLocale } from "./locales";
import { peekSessionHint } from "../lib/session-hint";
import { useAuth } from "../lib/auth";

export function LocaleRedirect() {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const appCode = useAppLocale();
  // Signing out on "/" turns the dashboard into the landing page in place.
  const { user, loading } = useAuth();
  const signedIn = !loading && !!user;

  useEffect(() => {
    if (appCode === DEFAULT_LOCALE) return;
    const { locale, path } = splitLocale(pathname);
    if (locale !== DEFAULT_LOCALE || !isTranslatedPath(path)) return;
    if (path === "/" && peekSessionHint()) return;
    navigate(localizePath(path, appCode) + search + hash, { replace: true });
  }, [pathname, search, hash, appCode, navigate, signedIn]);

  return null;
}
